import React, { createContext, useContext, useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import { api } from "../lib/api";
import { initTracking, trackPageview, getTrackingConfig } from "../lib/tracking";

const TrackingContext = createContext({ config: null, ready: false });

export function TrackingProvider({ children }) {
  const [config, setConfig] = useState(null);
  const [ready, setReady] = useState(false);
  const location = useLocation();
  const initialPv = useRef(false);

  // Fetch tracking config from backend (site_content key="tracking") and inject scripts
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/site-content/tracking");
        const value = (data && data.value) || {};
        if (!cancelled) {
          initTracking(value);
          setConfig(getTrackingConfig());
        }
      } catch (e) {
        // Silently ignore — tracking is optional, but log for debugging
        console.debug("[TrackingProvider] Failed to load tracking config:", e?.message);
      } finally {
        if (!cancelled) setReady(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // SPA pageview on every route change (after ready)
  useEffect(() => {
    if (!ready) return;
    // Skip the first effect if it's the same as initial mount — actually fire it
    if (!initialPv.current) {
      initialPv.current = true;
    }
    trackPageview(location.pathname + location.search);
  }, [ready, location.pathname, location.search]);

  return (
    <TrackingContext.Provider value={{ config, ready }}>
      {children}
    </TrackingContext.Provider>
  );
}

export function useTracking() {
  return useContext(TrackingContext);
}
